<template>
  <v-card
        class="elevation-8 border-radius-top"
        width="500"
  >
    <v-container
        fluid
        class="order-card-content"
        :class="(quantityInCart <= 0 || !showLineItems) ? 'order-card-closed' : 'order-card-open'"
    >
      <v-row
        align="center"
        :class="(quantityInCart > 0 && showLineItems) ? 'py-3' : ''"
        @click="handleAction"
        @touchend="handleAction"
      >
        <v-col cols="2">
          <v-icon
            v-show="quantityInCart <= 0"
            color="primary"
            icon="mdi-basket-outline"
            size="32"
          />
          <v-avatar
            v-show="quantityInCart > 0"
            class="bg-primary"
            :size="avatarSize"
          >
            <h3>
              {{ quantityInCart }}
            </h3>
          </v-avatar>
        </v-col>
        <v-col class="text-primary text-center">
          <h3>
            {{ t('My Order') }}
          </h3>
        </v-col>
        <v-col
          cols="2"
          class="text-primary text-end d-flex align-center justify-end"
        >
          <!-- Coupon icon - only show when cart has items -->
          <v-btn
            v-show="quantityInCart > 0"
            icon
            variant="text"
            size="small"
            color="primary"
            class="mr-2"
            @click.stop="openCouponDialog"
            @touchend.stop="openCouponDialog"
          >
            <v-icon
              size="28"
              icon="mdi-ticket-percent"
            />
          </v-btn>
          
          <v-icon
            size="32"
            icon="mdi-chevron-up"
            :class="(quantityInCart > 0 && showLineItems) ? 'transform-180' : ''"
          />
        </v-col>
      </v-row>
      <v-row
        v-show="quantityInCart > 0 && showLineItems"
        dense
        class="line-items-container"
      >
        <!-- Applied coupon display -->
        <v-col
          v-if="appliedCoupon"
          cols="12"
          class="pb-2"
        >
          <v-card
            variant="outlined"
            class="rounded-lg bg-green-lighten-5"
            style="border-color: #4CAF50;"
          >
            <div class="d-flex flex-row align-center px-4 py-2">
              <v-icon 
                icon="mdi-ticket-percent" 
                color="green" 
                size="20"
                class="mr-2"
              />
              <div class="flex-grow-1">
                <span class="text-green-darken-2 font-weight-medium">
                  {{ t('Coupon Applied') }}: {{ appliedCoupon }}
                </span>
              </div>
              <v-btn
                icon
                variant="text"
                size="small"
                color="green"
                @click="removeCoupon"
                @touchend="removeCoupon"
              >
                <v-icon icon="mdi-close" size="16" />
              </v-btn>
            </div>
          </v-card>
        </v-col>
      

        <v-col
          v-for="(lineItem, index) in orderLineItems"
          :key="lineItem.uuid"
          cols="12"
        >
          <LineItem
            :line-item="lineItem"
            :index="index"
          />
        </v-col>
      </v-row>
      <v-row v-show="quantityInCart > 0 && showLineItems">
        <v-col cols="12">
          <v-btn
            id="startCheckout"
            color="primary"
            size="110"
            block
            class="text-none order-card-button disable-pointer-events-inner"
            :disabled="!cartTotal || cartTotal.amount <= 0 || selecting"
            :loading="selecting"
            @click="startCheckout"
            @touchend="startCheckout"
          >
            <span class="body-text" style="padding-right: 36px;">
              {{ t('Pay') }}
            </span>
            <h2>
              {{ formatPrice(cartTotal) }}
            </h2>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import { useStore } from "../../stores/store";
import { useI18n } from "vue-i18n";
import LineItem from "./LineItem.vue";

export default {
  name: 'OrderCart',
  components: {
    LineItem
  },
  setup() {
    const store = useStore()
    const { t, te } = useI18n()

    return {
      store,
      t,
      te
    }
  },
  data() {
    return {
      selecting: false,
		clickedCart: false
    }
  },
  computed: {
    showLineItems() {
      return this.store.showingOrderLineItems
    },
    avatarSize() {
      if(this.quantityInCart > 99) {
        return '56'
      }
      if(this.quantityInCart > 9) {
        return '48'
      }
      return '40'
    },
    cartTotal() {
      return this.store.getCartTotal
    },
    order() {
      return this.store.order
    },
    orderLineItems() {
      if(typeof this.order !== 'undefined' && this.order !== null && Array.isArray(this.order.lineItems)) {
        return this.order.lineItems
      }
      return []
    },
    quantityInCart() {
      if(this.orderLineItems.length > 0) {
        return this.orderLineItems.reduce((sum, lineItem) => {
          if(typeof lineItem !== 'undefined' && lineItem !== null) {
            if (typeof lineItem.type !== 'undefined' && lineItem.type !== null && lineItem.type === 'event') {
              // Check if there are multiple variations selected.
              if(typeof lineItem.selectedVariations !== 'undefined' && lineItem.selectedVariations !== null && Array.isArray(lineItem.selectedVariations) && lineItem.selectedVariations.length > 1) {
                return sum + lineItem.selectedVariations.length
              }
            }
            if(typeof lineItem.quantity !== 'undefined' && lineItem.quantity !== null) {
              return sum + lineItem.quantity
            }
            return sum + 1
          }
          return sum
        }, 0)
      }
      return 0
    },
    appliedCoupon() {
      // Check if couponCalculation exists (indicates a coupon was successfully applied)
      if (this.store.couponCode && this.store.couponCalculation) {
        return this.store.couponCode
      }
      
      return null
    },
    selectedLocationPackagingType() {
      if(typeof this.order !== 'undefined' && this.order !== null) {
        if(typeof this.order.selectedLocationPackagingType !== 'undefined' && this.order.selectedLocationPackagingType !== null) {
          return this.order.selectedLocationPackagingType
        }
      }
      return null
    }
  },
  methods: {
    handleAction() {
		if(this.clickedCart === true) {
			return
		}

		this.clickedCart = true
		setTimeout(() => {
			if(this.quantityInCart > 0) {
				this.store.showingOrderLineItems = !this.store.showingOrderLineItems
			} else {
				this.showingOrderLineItems = false
			}
			this.clickedCart = false
		}, 100)
    },
    openCouponDialog() {
      this.store.openCouponDialog()
    },
    removeCoupon() {
      this.store.removeCoupon()
    },
    reset() {
      this.$emit('reset')
    },
    formatPrice(price) {
      return this.store.formatPrice(price)
    },
    changeShowLineItemsStatus() {
      this.showingOrderLineItems = !this.showingOrderLineItems
    },
    startCheckout() {
      this.selecting = true
      this.store.enableCheckout()
      this.store.customLogger('info', 'Starting checkout')
      setTimeout(() => {
        this.selecting = false
      }, 200)
    }
  },
  watch: {
    quantityInCart(currentValue) {
      if(currentValue <= 0) {
        this.showingOrderLineItems = false
      }
      if(currentValue > 0) {
        this.showingOrderLineItems = true
      }
    }
  }
}
</script>

<style scoped>
.open {
  transform: rotate(0deg);
  transition: transform 125ms linear;
}
.close {
  transform: rotate(-180deg);
  transition: transform 125ms linear;
}

.line-items-container {
  max-height: 40vh;
  overflow-y: auto;
  margin: 0;
  padding: 16px 16px;
}

/* Optional: Hide scrollbar for webkit browsers while maintaining functionality */
.line-items-container::-webkit-scrollbar {
  display: none;
}

/* Optional: Hide scrollbar for Firefox */
.line-items-container {
  scrollbar-width: none;
}
</style>